import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBillingDocuments, BillingDocument } from '@/hooks/useBillingDocuments';
import { BillingDocumentCard } from '@/components/billing/BillingDocumentCard';
import { BillingDocumentDialog } from '@/components/billing/BillingDocumentDialog';
import { BillingStatsCard } from '@/components/billing/BillingStatsCard';
import { generateBillingDocumentPDF } from '@/lib/pdfGenerator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Receipt, Loader2, Filter } from 'lucide-react';
import { Loader } from '@/components/ui/loader';
import { toast } from 'sonner';

type DocumentStatus = 'all' | 'draft' | 'sent' | 'paid' | 'cancelled';

export default function Billing() {
  const { t } = useTranslation();
  const { documents, isLoading } = useBillingDocuments();
  const [selectedDocument, setSelectedDocument] = useState<BillingDocument | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<DocumentStatus>('all');

  const handleViewDocument = (document: BillingDocument) => {
    setSelectedDocument(document);
    setDialogOpen(true);
  };

  const handleDownloadPDF = (document: BillingDocument) => {
    try {
      generateBillingDocumentPDF(document);
      toast.success(t('billing.messages.pdfSuccess'));
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('billing.messages.pdfError'));
    }
  };

  const filterByStatus = (docs: BillingDocument[]) => {
    if (statusFilter === 'all') return docs;
    return docs.filter(d => d.status === statusFilter);
  };

  const orderForms = filterByStatus(documents.filter(d => d.document_type === 'order_form'));
  const invoices = filterByStatus(documents.filter(d => d.document_type === 'invoice'));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-semibold text-[#37699E] font-['Lexend_Deca'] mb-2">
            {t('billing.title')}
          </h1>
          <p className="text-[#5A6B7A]">
            {t('billing.subtitle')}
          </p>
        </div>

        {/* Statistics Card */}
        <BillingStatsCard documents={documents} />

        {/* Tabs with Filter */}
        <Tabs defaultValue="order_forms" className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <TabsList className="bg-white border border-[#DBE2EA]">
              <TabsTrigger value="order_forms" className="data-[state=active]:bg-[#0880AE] data-[state=active]:text-white">
                <FileText size={16} className="mr-2" />
                {t('billing.tabs.orderForms')} ({orderForms.length})
              </TabsTrigger>
              <TabsTrigger value="invoices" className="data-[state=active]:bg-[#0880AE] data-[state=active]:text-white">
                <Receipt size={16} className="mr-2" />
                {t('billing.tabs.invoices')} ({invoices.length})
              </TabsTrigger>
            </TabsList>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-2">
                <Filter size={14} className="inline mr-2" />
                {t('billing.filterByStatus')}
              </label>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as DocumentStatus)}>
                <SelectTrigger className="w-full sm:w-64 bg-white border-[#DBE2EA]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('billing.allStatuses')}</SelectItem>
                  <SelectItem value="draft">{t('billing.statuses.draft')}</SelectItem>
                  <SelectItem value="sent">{t('billing.statuses.sent')}</SelectItem>
                  <SelectItem value="paid">{t('billing.statuses.paid')}</SelectItem>
                  <SelectItem value="cancelled">{t('billing.statuses.cancelled')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <TabsContent value="order_forms" className="space-y-4">
            {orderForms.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-[#DBE2EA]">
                <FileText className="mx-auto h-12 w-12 text-[#87ADD4] mb-4" />
                <h3 className="text-lg font-medium text-[#2D3748] mb-2">
                  {t('billing.emptyStates.orderForms.title')}
                </h3>
                <p className="text-[#5A6B7A]">
                  {t('billing.emptyStates.orderForms.description')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {orderForms.map(document => (
                  <BillingDocumentCard
                    key={document.id}
                    document={document}
                    onView={handleViewDocument}
                    onDownload={handleDownloadPDF}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="invoices" className="space-y-4">
            {invoices.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-[#DBE2EA]">
                <Receipt className="mx-auto h-12 w-12 text-[#87ADD4] mb-4" />
                <h3 className="text-lg font-medium text-[#2D3748] mb-2">
                  {t('billing.emptyStates.invoices.title')}
                </h3>
                <p className="text-[#5A6B7A]">
                  {t('billing.emptyStates.invoices.description')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {invoices.map(document => (
                  <BillingDocumentCard
                    key={document.id}
                    document={document}
                    onView={handleViewDocument}
                    onDownload={handleDownloadPDF}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Document Detail Dialog */}
        <BillingDocumentDialog
          document={selectedDocument}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onDownload={handleDownloadPDF}
        />
      </div>
  );
}
